import { PriceModel } from 'types/purchasing';
import {
  PurchaseRule,
  StockLocation,
  StockLocationLite,
} from 'types/inventory';
import { CustomPropertiesType, DateString } from 'types';
import { TeamLite, TeamType } from 'types/properties';
import {
  InvoiceApprovalRules,
  UpdatePurchaseLinePricesFromBill,
} from 'types/suppliers';
import { GenericResource } from 'types/api';

export interface OrgAddressBase {
  line1: string | null | undefined;
  line2: string | null | undefined;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  company: string | null | undefined;
  name: string | null | undefined;
  phone: string | null | undefined;
}

export interface OrgAddressFull extends OrgAddressBase {
  id: number;
  org: OrgLite;
  isDefaultShipping?: boolean;
  isDefaultBilling?: boolean;
  flag: string;
}

export interface Lookup {
  orgName: string | null;
  orgImage: string | null;
  id: string;
  org: string;
  orgs: string[];
  saml: string | null;
  sandbox?: boolean;
}

export enum OrderApprovalThresholdDirection {
  UP = 'up',
  DOWN = 'down',
}

export enum PunchoutCreate {
  PURCHASE = 'purchase',
  PURCHASE_REQUEST_GROUP = 'purchase_request_group',
}

export enum PoRequirementType {
  NONE = 'none',
  PURCHASE_REQUEST_EXTERNAL = 'purchase_request_external',
  PURCHASE_REQUEST = 'purchase_request',
  PO_EXTERNAL = 'po_external',
  PO = 'po',
}

export enum PoRequirementTypeSimplifiedBuying {
  NONE = 'none',
  PURCHASE_REQUEST_EXTERNAL = 'purchase_request_external',
  PURCHASE_REQUEST = 'purchase_request',
  PO = 'po',
}

export const PoRequirementTypeLabels = {
  [PoRequirementType.NONE]: 'None',
  [PoRequirementType.PURCHASE_REQUEST_EXTERNAL]: 'Requisition PO Number',
  [PoRequirementType.PURCHASE_REQUEST]: 'Requisition Code',
  [PoRequirementType.PO_EXTERNAL]: 'Customer PO Number',
  [PoRequirementType.PO]: 'PO Number',
};

export const PoRequirementTypeLabelsSimplifiedBuying = {
  [PoRequirementTypeSimplifiedBuying.NONE]: 'None',
  [PoRequirementTypeSimplifiedBuying.PURCHASE_REQUEST_EXTERNAL]:
    'Your PO Number Entered for the Order',
  [PoRequirementTypeSimplifiedBuying.PURCHASE_REQUEST]:
    "Cofactr's Auto-Generated Order Number",
  [PoRequirementTypeSimplifiedBuying.PO]:
    "Cofactr's Auto-Generated Supplier PO Number",
};

export type CreditCardPaymentSource = {
  brand: 'Visa' | 'Mastercard' | 'American Express' | string;
  expMonth: number;
  expYear: number;
  last4: string;
  object: 'card';
  id: number;
};

export type BankPaymentSource = {
  bankName: string;
  id: number;
  last4: string;
  routingNumber: string;
  object: 'bank_account';
  verified: boolean;
};

export type PaymentSource = CreditCardPaymentSource | BankPaymentSource;

export interface Org extends GenericResource {
  id: string;
  slug: string;
  code: string;
  name: string;
  image: string | null;
  timezone: string | null;
  mslResetOnReceipt: boolean;
  lcrInspectionOnReceipt: boolean;
  inspectionPhotoOnReceipt: boolean;
  packagePhotoOnReceipt: boolean;
  dimensionalInspectionOnReceipt: boolean;
  permissions: string[];
  website: string;
  priceModel: PriceModel;
  sandbox?: boolean;
  defaultShippingService: string;
  defaultShippingCarrier: string;
  createdFrom: Org | Org['id'] | null;
  parentOrgName: string | null;
  defaultShippingDestinationLocation: StockLocation | null;
  defaultShippingPartial: boolean;
  poFooterNotes?: string;
  bomMissingCustomIdWarning?: string;
  bomUploadCustomId?: string;
  bomClearCustomIdOnAltUnapprove?: boolean;
  bomAddCustomIdOnAltApprove?: boolean;
  bomTransferCustomIdOnEdit?: boolean;
  loggingEnabled?: boolean;
  internalMetadata?: object;
  externalMetadata?: object;
  orderApprovalThreshold?: string;
  orderApprovalThresholdDirection?: OrderApprovalThresholdDirection;
  defaultPurchaseRule?: PurchaseRule | null;
  industry?: string | null;
  militaryUse?: boolean;
  onboardingRequired?: boolean;
  onboardingCalendlyUrl?: string;
  trialEndsOn?: DateString | null;
  planExpiresOn?: DateString | null;
  dpas?: string | null;
  upsAccountNumber?: string | null;
  upsAccountCountry?: string | null;
  upsAccountPostalCode?: string | null;
  fedexAccountNumber?: string | null;
  fedexAccountCountry?: string | null;
  fedexAccountPostalCode?: string | null;
  billingEmail?: string | null;
  poRequiredMessage?: string | null;
  billingAddressLine1?: string | null;
  billingAddressLine2?: string | null;
  billingAddressCity?: string | null;
  billingAddressState?: string | null;
  billingAddressCountry?: string | null;
  billingAddressPostalCode?: string | null;
  billingAddressName?: string | null;
  availablePurchaseAmount?: number | null;
  allowNoTeamShipments?: boolean;
  defaultTeamType?: TeamType;
  docaiFilterWhitelistSuppliers?: boolean;
  docaiFilterDomainWhitelist?: string[];
  docaiFilterDomainBlacklist?: string[];
  docaiFilterSubjectRegexWhitelist?: string[];
  docaiFilterSubjectRegexBlacklist?: string[];
  docaiFilterBodyRegexWhitelist?: string[];
  docaiFilterBodyRegexBlacklist?: string[];
  docaiFilterAllowIfRecordMatches?: boolean;
  docaiFilterIgnoreIfRecordNotMatched?: boolean;
  docaiAutosubmit?: boolean;
  docaiManagedByOrg?: string | null;
  customProperties: CustomPropertiesType;
  invoiceApproval: InvoiceApprovalRules;
  updatePurchaseLinePricesFromBill: UpdatePurchaseLinePricesFromBill;
  ignoreShippingInBillApproval: boolean;
  updatePurchaseShippingFromBill: boolean;
  ignoreDutiesInBillApproval: boolean;
  updatePurchaseDutiesFromBill: boolean;
  ignoreTaxesInBillApproval: boolean;
  updatePurchaseTaxesFromBill: boolean;
  onlyUseTotalForBillApproval: boolean;
  punchoutsCreate: PunchoutCreate;
  onlyOrgPartLibrary: boolean;
  defaultCofactrWarehouse?: StockLocationLite | null;
  requireAdminApprovals?: boolean;
  allowSelfManagedPurchasing?: boolean;
  allowCofactrManagedPurchasing?: boolean;
  allowableCofactrManagedEstimateOverage?: string;
  poRequirementType?: PoRequirementType;
  customPoPrefix?: string;
  customPoNumberLength?: number;
  autoApproveSupplierQuotes?: boolean;
  aggregateInvoicesByPoNumber?: boolean;
  aggregateInvoicesByRequisition?: boolean;
  aggregateInvoicesBySupplierOrder?: boolean;
  aggregateInvoicesByTeam?: boolean;
  aggregateInvoicesByProgram?: boolean;
  aggregateInvoicesTimeframe?: InvoiceAggregationTimeframe;
  blanketPartsPo?: string | null;
  blanketSubscriptionPo?: string | null;
  useManagedInventory?: boolean;
  active?: boolean;
  paymentTerms?: PaymentTerms | null;
  paymentSource?: PaymentSource | null;
  useTeamPermissions?: boolean;
}

export type EntitlementApp = {
  id: string;
  name: string;
  slug: string;
  description: string;
};

export type EntitlementFeature = {
  id: string;
  name: string;
  slug: string;
  description: string;
  app: EntitlementApp['id'];
};

export type EntitlementPlan = {
  id: string;
  name: string;
  description: string;
  apps: EntitlementApp[];
  features: EntitlementFeature[];
};

export interface OrgWithEntitlements extends Org {
  entitlementPlan: EntitlementPlan;
}

export enum InvoiceAggregationTimeframe {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export const invoiceAggregationTimeframeLabels = {
  [InvoiceAggregationTimeframe.DAILY]: 'Daily',
  [InvoiceAggregationTimeframe.WEEKLY]: 'Weekly',
  [InvoiceAggregationTimeframe.MONTHLY]: 'Monthly',
};

export enum PaymentTerms {
  NET_7 = 'net_7',
  NET_14 = 'net_14',
  NET_15 = 'net_15',
  NET_30 = 'net_30',
  NET_45 = 'net_45',
  NET_60 = 'net_60',
  NET_90 = 'net_90',
  AUTO_PAY = 'auto_pay',
  ON_RECEIPT = 'on_receipt',
  NOT_SET = 'not_set',
}

export const paymentTermsLabels = {
  [PaymentTerms.NET_7]: 'NET 7',
  [PaymentTerms.NET_14]: 'NET 14',
  [PaymentTerms.NET_15]: 'NET 15',
  [PaymentTerms.NET_30]: 'NET 30',
  [PaymentTerms.NET_45]: 'NET 45',
  [PaymentTerms.NET_60]: 'NET 60',
  [PaymentTerms.NET_90]: 'NET 90',
};

export interface OrgLite {
  id: string;
  name: string;
  permissions: string[];
  sandbox?: boolean;
  poFooterNotes?: string;
  bomMissingCustomIdWarning?: string;
  bomUploadCustomId?: string;
  bomClearCustomIdOnAltUnapprove?: boolean;
  bomAddCustomIdOnAltApprove?: boolean;
  bomTransferCustomIdOnEdit?: boolean;
  loggingEnabled?: boolean;
  industry?: string | null;
  militaryUse?: boolean;
  onboardingRequired?: boolean;
  trialEndsOn?: DateString | null;
  planExpiresOn?: DateString | null;
  dpas?: string | null;
  upsAccountNumber?: string | null;
  upsAccountCountry?: string | null;
  upsAccountPostalCode?: string | null;
  fedexAccountNumber?: string | null;
  fedexAccountCountry?: string | null;
  fedexAccountPostalCode?: string | null;
  billingEmail?: string | null;
  billingAddressLine1?: string | null;
  billingAddressLine2?: string | null;
  billingAddressCity?: string | null;
  billingAddressState?: string | null;
  billingAddressCountry?: string | null;
  billingAddressPostalCode?: string | null;
  billingAddressName?: string | null;
  docaiFilterWhitelistSuppliers?: boolean;
  docaiFilterDomainWhitelist?: string[];
  docaiFilterDomainBlacklist?: string[];
  docaiFilterSubjectRegexWhitelist?: string[];
  docaiFilterSubjectRegexBlacklist?: string[];
  docaiFilterBodyRegexWhitelist?: string[];
  docaiFilterBodyRegexBlacklist?: string[];
  docaiFilterAllowIfRecordMatches?: boolean;
  docaiFilterIgnoreIfRecordNotMatched?: boolean;
  docaiAutosubmit?: boolean;
  docaiManagedByOrg?: string | null;
  invoiceApproval: InvoiceApprovalRules;
  updatePurchaseLinePricesFromBill: UpdatePurchaseLinePricesFromBill;
  ignoreShippingInBillApproval: boolean;
  updatePurchaseShippingFromBill: boolean;
  ignoreDutiesInBillApproval: boolean;
  updatePurchaseDutiesFromBill: boolean;
  ignoreTaxesInBillApproval: boolean;
  updatePurchaseTaxesFromBill: boolean;
  onlyUseTotalForBillApproval: boolean;
  defaultCofactrWarehouse?: StockLocationLite['id'] | null;
  requireAdminApprovals?: boolean;
  onlyOrgPartLibrary?: boolean;
  allowSelfManagedPurchasing?: boolean;
  allowCofactrManagedPurchasing?: boolean;
  allowableCofactrManagedEstimateOverage?: string;
  poRequirementType?: PoRequirementType;
  customPoPrefix?: string;
  customPoNumberLength?: number;
  autoApproveSupplierQuotes?: boolean;
  aggregateInvoicesByPoNumber?: boolean;
  aggregateInvoicesByRequisition?: boolean;
  aggregateInvoicesBySupplierOrder?: boolean;
  aggregateInvoicesByTeam?: boolean;
  aggregateInvoicesByProgram?: boolean;
  aggregateInvoicesTimeframe?: InvoiceAggregationTimeframe;
  blanketPartsPo?: string | null;
  blanketSubscriptionPo?: string | null;
  useManagedInventory?: boolean;
  active?: boolean;
  paymentTerms?: PaymentTerms | null;
  paymentSource?: PaymentSource | null;
  useTeamPermissions?: boolean;
}

export interface OrgStateProps {
  activeOrgId: Org['id'] | null;
  activeOrg: Org | null;
  blended: boolean;
  sandbox: boolean;
  punchoutId?: string | null;
}

export interface OrgPatch {
  name?: string;
  website?: string;
  mslResetOnReceipt?: boolean;
  lcrInspectionOnReceipt?: boolean;
  dimensionalInspectionOnReceipt?: boolean;
  inspectionPhotoOnReceipt?: boolean;
  packagePhotoOnReceipt?: boolean;
  timezone?: string | null;
  defaultShippingService?: string;
  defaultShippingCarrier?: string;
  defaultShippingDestinationLocation?: StockLocation['id'] | null;
  defaultShippingPartial?: boolean;
  poFooterNotes?: string;
  bomMissingCustomIdWarning?: string;
  bomUploadCustomId?: string;
  bomClearCustomIdOnAltUnapprove?: boolean;
  bomAddCustomIdOnAltApprove?: boolean;
  bomTransferCustomIdOnEdit?: boolean;
  industry?: string | null;
  militaryUse?: boolean;
  onboardingRequired?: boolean;
  trialEndsOn?: DateString | null;
  planExpiresOn?: DateString | null;
  dpas?: string | null;
  upsAccountNumber?: string | null;
  upsAccountCountry?: string | null;
  upsAccountPostalCode?: string | null;
  fedexAccountNumber?: string | null;
  fedexAccountCountry?: string | null;
  fedexAccountPostalCode?: string | null;
  billingEmail?: string | null;
  billingAddressLine1?: string | null;
  billingAddressLine2?: string | null;
  billingAddressCity?: string | null;
  billingAddressState?: string | null;
  billingAddressCountry?: string | null;
  billingAddressPostalCode?: string | null;
  billingAddressName?: string | null;
  allowNoTeamShipments?: boolean;
  defaultTeamType?: TeamType['id'];
  docaiFilterWhitelistSuppliers?: boolean;
  docaiFilterDomainWhitelist?: string[];
  docaiFilterDomainBlacklist?: string[];
  docaiFilterSubjectRegexWhitelist?: string[];
  docaiFilterSubjectRegexBlacklist?: string[];
  docaiFilterBodyRegexWhitelist?: string[];
  docaiFilterBodyRegexBlacklist?: string[];
  docaiFilterAllowIfRecordMatches?: boolean;
  docaiFilterIgnoreIfRecordNotMatched?: boolean;
  invoiceApproval?: InvoiceApprovalRules;
  updatePurchaseLinePricesFromBill?: UpdatePurchaseLinePricesFromBill;
  ignoreShippingInBillApproval?: boolean;
  updatePurchaseShippingFromBill?: boolean;
  ignoreDutiesInBillApproval?: boolean;
  updatePurchaseDutiesFromBill?: boolean;
  ignoreTaxesInBillApproval?: boolean;
  updatePurchaseTaxesFromBill?: boolean;
  onlyUseTotalForBillApproval?: boolean;
  defaultCofactrWarehouse?: StockLocationLite['id'] | null;
  requireAdminApprovals?: boolean;
  onlyOrgPartLibrary?: boolean;
  allowSelfManagedPurchasing?: boolean;
  allowCofactrManagedPurchasing?: boolean;
  allowableCofactrManagedEstimateOverage?: string;
  poRequirementType?: PoRequirementType;
  customPoPrefix?: string;
  customPoNumberLength?: number;
  poRequiredMessage?: string | null;
  autoApproveSupplierQuotes?: boolean;
  aggregateInvoicesByPoNumber?: boolean;
  aggregateInvoicesByRequisition?: boolean;
  aggregateInvoicesBySupplierOrder?: boolean;
  aggregateInvoicesByTeam?: boolean;
  aggregateInvoicesByProgram?: boolean;
  aggregateInvoicesTimeframe?: InvoiceAggregationTimeframe;
  blanketPartsPo?: string | null;
  blanketSubscriptionPo?: string | null;
  useTeamPermissions?: boolean;
}

export interface OrgUserCreate {
  firstName: string;
  lastName: string;
  email: string;
  title?: string;
  allTeamsAccess?: boolean;
}
export interface OrgUserCreateResponse {
  firstName: string;
  magicLink: string;
  orgName: string;
  adminEmail: string;
  userId: string;
}
export interface OrgUserUpdate {
  firstName?: string;
  lastName?: string;
  title?: string | null;
  allTeamsAccess?: boolean;
}
export interface OrgUserUpdateResponse {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  permissions: string[];
  title: string | null;
}
export interface OrgUserFull extends GenericResource {
  id: string;
  name: string;
  slug: string;
  image: string | null | undefined;
  firstName: string;
  lastName: string;
  email: string;
  title: string | null;
  isInternal?: boolean;
  isStaff?: boolean;
  allTeamsAccess?: boolean;
  teams?: TeamLite[];
  accessRoleAssignments?: {
    id: string;
    accessRoleId: string;
    org: string;
    isBase: boolean;
    name: string;
  }[];
}

export interface CreateSubOrgRequest {
  name: string;
  website?: string;
  timezone?: string | null;
  industry?: string | null;
  militaryUse?: boolean;
  sandbox?: boolean;
}

export interface OrgSignupRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  title: string | null;
  orgName: string;
  orgWebsite?: string;
  industry?: string | null;
  militaryUse?: boolean;
  onboardingRequired?: boolean;
  billingEmail?: string | null;
  billingAddressLine1?: string | null;
  billingAddressLine2?: string | null;
  billingAddressCity?: string | null;
  billingAddressState?: string | null;
  billingAddressCountry?: string | null;
  billingAddressPostalCode?: string | null;
  billingAddressName?: string | null;
  creditCardToken?: string | null;
  achToken?: string | null;
  entitlementPlanId?: string | null;
  planExpiresIn?: number | null;
  trialConvertsIn?: number | null;
  calendlyUrl?: string | null;
}
